/**
 * Weapons: fire control (exact fractional rates at 30 Hz, magazines, bursts, spin-up, bloom),
 * hitscan pellets, projectile spawning, beam ticks, and the melee state machine. Shots start at
 * the aim origin on the mech axis and travel along the aim; the muzzle only positions the tracer.
 */
import { SIM_DT } from '../constants.js';
import { angleBetween, degToRad, vNormalize } from '../sim/math.js';
import { rngFloat } from '../sim/rng.js';
import { addHeat, weaponsLocked } from './resources.js';
import { applyDamage, areaPacket, breakCloak, falloffFactor, pointPacket } from './damage.js';
import { damageDeployable } from './deployable-damage.js';
import { knockbackFrom, refreshEffect } from './effects.js';
import { capsuleInCone, closestPointOnAxis } from './geometry.js';
import { aimDir, aimOrigin, combatantCapsule, muzzlePosition, poseCapsule, secondsToTicks, } from './state.js';
import { defOf, emit, isLive, mech, nextId, targetPose } from './tick.js';
import { traceShot } from './trace.js';
import { otherSlot } from './types.js';
import { meleeWeaponFor, weaponOf } from './catalog.js';
/** Most shots one weapon may fire in a single tick (a 25/s gatling needs at most one extra). */
const MAX_SHOTS_PER_TICK = 4;
/** Random direction inside a cone around `dir` (uniform over the cone's solid angle). */
export function spreadDirection(t, dir, halfAngle) {
    if (halfAngle <= 0)
        return dir;
    const angle = halfAngle * Math.sqrt(rngFloat(t.s.rng));
    const azimuth = Math.PI * 2 * rngFloat(t.s.rng);
    // Orthonormal basis around dir.
    const helper = Math.abs(dir.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
    const right = vNormalize({
        x: dir.y * helper.z - dir.z * helper.y,
        y: dir.z * helper.x - dir.x * helper.z,
        z: dir.x * helper.y - dir.y * helper.x,
    });
    const up = {
        x: right.y * dir.z - right.z * dir.y,
        y: right.z * dir.x - right.x * dir.z,
        z: right.x * dir.y - right.y * dir.x,
    };
    const s = Math.sin(angle);
    const c = Math.cos(angle);
    const ca = Math.cos(azimuth);
    const sa = Math.sin(azimuth);
    return vNormalize({
        x: dir.x * c + (right.x * ca + up.x * sa) * s,
        y: dir.y * c + (right.y * ca + up.y * sa) * s,
        z: dir.z * c + (right.z * ca + up.z * sa) * s,
    });
}
function applyOnHit(t, weapon, target, source) {
    for (const effect of weapon.onHit) {
        const p = effect.params;
        refreshEffect(t, target, effect.kind, source, secondsToTicks(effect.seconds), {
            ...(p.speedScale !== undefined ? { speedScale: p.speedScale } : {}),
            ...(p.damageTakenScale !== undefined ? { damageTakenScale: p.damageTakenScale } : {}),
            ...(p.dot ? { dot: { damagePerSecond: p.dot.damagePerSecond, kind: 'burn' } } : {}),
            ...(p.heatPerSecond !== undefined ? { hot: { heat: -p.heatPerSecond } } : {}),
        });
    }
    if (weapon.targetHeat > 0)
        addHeat(t, target, weapon.targetHeat);
    if (weapon.revealsCloak)
        breakCloak(t, target);
}
/** Fires every pellet of a hitscan weapon from `origin` along the aim, tracers from `muzzle`. */
export function fireHitscan(t, shooter, weapon, origin, baseDir, spreadHalfAngle, muzzle, options = {}) {
    const pellets = weapon.hitscan?.pellets ?? 1;
    const scale = options.damageScale ?? 1;
    const countStats = options.countStats ?? true;
    let hits = 0;
    for (let i = 0; i < pellets; i++) {
        const dir = spreadDirection(t, baseDir, spreadHalfAngle);
        // Turret fire has no viewer to compensate for: it resolves against the live pose.
        const trace = traceShot(t, shooter, origin, dir, weapon.range.max, {
            rewind: options.kind !== 'turret',
        });
        if (countStats)
            t.s.stats[shooter].shotsFired++;
        if (trace.hit !== null) {
            const amount = weapon.damage * falloffFactor(trace.distance, weapon.range) * scale;
            applyDamage(t, pointPacket(t, trace.hit, shooter, amount, options.kind ?? 'hitscan', trace.point, origin, trace.targetPose));
            applyOnHit(t, weapon, trace.hit, shooter);
            if (countStats)
                t.s.stats[shooter].shotsHit++;
            hits++;
        }
        else if (trace.deployable) {
            damageDeployable(t, trace.deployable, weapon.damage * falloffFactor(trace.distance, weapon.range) * scale, shooter);
        }
        emit(t, {
            t: 'shot',
            slot: shooter,
            weapon: weapon.id,
            origin: muzzle,
            end: trace.point,
            hit: trace.hit,
            blocked: trace.blocked,
        });
    }
    return hits;
}
export function spawnProjectile(t, shooter, weapon, position, dir, options = {}) {
    const spec = weapon.projectile;
    if (!spec)
        throw new Error(`weapon "${weapon.id}" is not a projectile`);
    const speed = options.speed ?? spec.speed;
    let homingTarget = options.homingTarget ?? null;
    if (spec.homing && homingTarget === null) {
        // Acquire the enemy when the launch direction points within the acquisition cone.
        const enemy = otherSlot(shooter);
        const target = mech(t, enemy);
        if (target.alive) {
            const c = target.body.position;
            const to = vNormalize({
                x: c.x - position.x,
                y: c.y + defOf(t, enemy).capsule.height / 2 - position.y,
                z: c.z - position.z,
            });
            if (angleBetween(dir, to) <= degToRad(spec.homing.acquireDeg))
                homingTarget = enemy;
        }
    }
    const projectile = {
        id: nextId(t),
        slot: shooter,
        weapon: weapon.id,
        position: { ...position },
        velocity: { x: dir.x * speed, y: dir.y * speed, z: dir.z * speed },
        ticksLeft: secondsToTicks(spec.lifeSeconds),
        damageScale: options.damageScale ?? 1,
        homingTarget,
        chainsLeft: spec.chain?.maxJumps ?? 0,
        reflected: false,
        groundY: options.groundY ?? null,
    };
    t.s.projectiles.push(projectile);
    if (options.countStats ?? true)
        t.s.stats[shooter].shotsFired++;
    emit(t, {
        t: 'projectileSpawn',
        id: projectile.id,
        slot: shooter,
        weapon: weapon.id,
        position: { ...projectile.position },
        velocity: { ...projectile.velocity },
    });
    return projectile;
}
/** One tick of a beam: trace with the beam width, damage for this tick, tracer event. */
export function beamTick(t, shooter, weapon, origin, dir, muzzle, options = {}) {
    const spec = weapon.beam;
    if (!spec)
        throw new Error(`weapon "${weapon.id}" is not a beam`);
    const trace = traceShot(t, shooter, origin, dir, weapon.range.max, {
        width: spec.width,
        pierce: spec.pierce,
    });
    const countStats = options.countStats ?? true;
    if (countStats)
        t.s.stats[shooter].shotsFired++;
    const scale = options.damageScale ?? 1;
    const perTick = weapon.damage * SIM_DT * scale;
    if (trace.deployable && (spec.pierce || trace.hit === null)) {
        damageDeployable(t, trace.deployable, perTick * falloffFactor(trace.distance, weapon.range), shooter);
    }
    if (trace.hit !== null) {
        const amount = perTick * falloffFactor(trace.distance, weapon.range);
        const victim = mech(t, trace.hit);
        const before = victim.hp + victim.shield;
        applyDamage(t, pointPacket(t, trace.hit, shooter, amount, 'beam', trace.point, origin, trace.targetPose));
        const landed = before - (victim.hp + victim.shield) > 0;
        applyOnHit(t, weapon, trace.hit, shooter);
        if (countStats)
            t.s.stats[shooter].shotsHit++;
        if (options.siphon && landed) {
            const self = mech(t, shooter);
            const def = defOf(t, shooter);
            self.hp = Math.min(def.hp, self.hp + options.siphon.hpPerSecond * SIM_DT);
            const target = mech(t, trace.hit);
            const stolen = Math.min(target.body.energy, options.siphon.energyPerSecond * SIM_DT);
            target.body.energy -= stolen;
            self.body.energy = Math.min(def.energy, self.body.energy + stolen);
        }
    }
    emit(t, {
        t: 'beam',
        slot: shooter,
        weapon: weapon.id,
        origin: muzzle,
        end: trace.point,
        hit: trace.hit,
    });
    return trace.hit;
}
function cloakBreaksOnFire(t, slot) {
    const c = mech(t, slot);
    if (c.effects.some((e) => e.params.cloak?.breakOnFire))
        breakCloak(t, slot);
}
/** Spread half-angle in radians for a hitscan weapon at its current bloom and the mech's spread scale. */
export function currentSpread(t, slot, weapon, w) {
    const h = weapon.hitscan;
    if (!h)
        return 0;
    return degToRad(h.spreadDeg + h.maxBloomDeg * w.bloom) * t.mods[slot].spreadScale;
}
function fireOne(t, slot, weapon, w) {
    const c = mech(t, slot);
    const def = defOf(t, slot);
    const origin = aimOrigin(c.body, def, t.ctx.balance.aimHeightFraction);
    const dir = aimDir(c.body);
    const muzzle = muzzlePosition(c.body, weapon.muzzle);
    addHeat(t, slot, weapon.heat);
    if (w.ammo !== null)
        w.ammo--;
    cloakBreaksOnFire(t, slot);
    if (weapon.kind === 'hitscan') {
        fireHitscan(t, slot, weapon, origin, dir, currentSpread(t, slot, weapon, w), muzzle);
        w.bloom = Math.min(1, w.bloom + (weapon.hitscan?.bloomPerShot ?? 0));
    }
    else if (weapon.kind === 'projectile') {
        const start = {
            x: origin.x + dir.x * 1.5,
            y: origin.y + dir.y * 1.5,
            z: origin.z + dir.z * 1.5,
        };
        spawnProjectile(t, slot, weapon, start, dir);
    }
}
/** Advances one weapon runtime for the tick. */
export function stepWeapon(t, slot, w, trigger) {
    const weapon = weaponOf(t.ctx, w.id);
    const c = mech(t, slot);
    const m = t.mods[slot];
    w.held = trigger;
    const locked = weaponsLocked(t, slot) || c.melee.phase !== 'idle';
    if (weapon.hitscan) {
        w.bloom = Math.max(0, w.bloom - weapon.hitscan.bloomDecayPerSecond * SIM_DT);
    }
    if (weapon.kind === 'melee') {
        // A held trigger keeps attacking; startMelee itself waits for the previous swing and cooldown.
        if (trigger && !locked)
            startMelee(t, slot);
        return;
    }
    if (weapon.kind === 'beam') {
        const spec = weapon.beam;
        if (w.beamCoolTicks > 0) {
            w.beamCoolTicks--;
            w.beamTicks = 0;
            return;
        }
        if (trigger && !locked) {
            w.beamTicks++;
            if (w.beamTicks > secondsToTicks(spec.warmupSeconds)) {
                const def = defOf(t, slot);
                const origin = aimOrigin(c.body, def, t.ctx.balance.aimHeightFraction);
                const dir = aimDir(c.body);
                cloakBreaksOnFire(t, slot);
                beamTick(t, slot, weapon, origin, dir, muzzlePosition(c.body, weapon.muzzle));
                addHeat(t, slot, weapon.heat * SIM_DT);
            }
            if (spec.maxSeconds !== null && w.beamTicks >= secondsToTicks(spec.maxSeconds)) {
                w.beamTicks = 0;
                w.beamCoolTicks = secondsToTicks(spec.coolSeconds);
            }
        }
        else {
            w.beamTicks = 0;
        }
        return;
    }
    // --- magazines ---
    if (w.reloadTicks > 0) {
        w.reloadTicks--;
        if (w.reloadTicks === 0 && weapon.magazine)
            w.ammo = weapon.magazine.size;
    }
    else if (weapon.magazine && w.ammo === 0) {
        w.reloadTicks = secondsToTicks(weapon.magazine.reloadSeconds);
        emit(t, { t: 'reload', slot, weapon: weapon.id, seconds: weapon.magazine.reloadSeconds });
    }
    // --- spin-up ---
    const spinUp = weapon.spinUpSeconds;
    if (spinUp > 0) {
        w.spin =
            trigger && !locked
                ? Math.min(1, w.spin + SIM_DT / spinUp)
                : Math.max(0, w.spin - (2 * SIM_DT) / spinUp);
    }
    else {
        w.spin = 1;
    }
    const canFire = !locked && w.reloadTicks === 0 && (w.ammo === null || w.ammo > 0);
    const rate = weapon.fireRate * m.fireRateScale * (spinUp > 0 ? w.spin : 1);
    const interval = rate > 1e-6 ? 1 / rate : Infinity;
    const burst = weapon.burst;
    if (w.burstLeft > 0) {
        // A burst in progress continues regardless of the trigger.
        w.cooldown -= SIM_DT;
        let shots = 0;
        while (w.burstLeft > 0 && w.cooldown <= 0 && canFire && shots < MAX_SHOTS_PER_TICK) {
            fireOne(t, slot, weapon, w);
            w.burstLeft--;
            w.cooldown += w.burstLeft > 0 ? burst.intervalSeconds : interval;
            shots++;
            if (w.ammo === 0)
                w.burstLeft = 0;
        }
        if (!canFire)
            w.burstLeft = 0;
        return;
    }
    if (trigger && canFire && (spinUp === 0 || w.spin >= 0.05)) {
        w.cooldown -= SIM_DT;
        let shots = 0;
        while (w.cooldown <= 0 && shots < MAX_SHOTS_PER_TICK && (w.ammo === null || w.ammo > 0)) {
            fireOne(t, slot, weapon, w);
            shots++;
            if (burst) {
                w.burstLeft = burst.count - 1;
                w.cooldown += burst.intervalSeconds;
                break;
            }
            w.cooldown += interval;
            if (!Number.isFinite(w.cooldown)) {
                w.cooldown = 0;
                break;
            }
        }
    }
    else {
        w.cooldown = Math.max(0, w.cooldown - SIM_DT);
    }
}
export function stepWeapons(t, slot, input) {
    const c = mech(t, slot);
    const def = defOf(t, slot);
    for (let i = 0; i < c.weapons.length; i++) {
        const w = c.weapons[i];
        const slotName = def.weapons[i]?.slot ?? 'primary';
        const trigger = slotName === 'primary' ? input.primary : input.secondary;
        stepWeapon(t, slot, w, trigger);
    }
}
// --- melee ---
export function startMelee(t, slot) {
    const c = mech(t, slot);
    if (c.melee.phase !== 'idle' || c.melee.cooldown > 0 || weaponsLocked(t, slot))
        return false;
    const weapon = meleeWeaponFor(t.ctx, defOf(t, slot));
    c.melee.phase = 'windup';
    c.melee.ticks = 0;
    c.melee.hit = false;
    addHeat(t, slot, weapon.heat);
    cloakBreaksOnFire(t, slot);
    return true;
}
function meleeHit(t, slot, weapon) {
    const c = mech(t, slot);
    const def = defOf(t, slot);
    const spec = weapon.melee;
    const origin = aimOrigin(c.body, def, t.ctx.balance.aimHeightFraction);
    const dir = aimDir(c.body);
    const half = degToRad(spec.arcDeg / 2);
    const enemy = otherSlot(slot);
    const target = mech(t, enemy);
    let hit = null;
    if (target.alive) {
        // The swing lands where the attacker saw the enemy; the knockback moves the live body.
        const pose = targetPose(t, slot);
        const cap = poseCapsule(pose, defOf(t, enemy));
        if (capsuleInCone(origin, dir, half, spec.reach, cap)) {
            const point = closestPointOnAxis(origin, cap);
            const dealt = applyDamage(t, pointPacket(t, enemy, slot, weapon.damage, 'melee', point, c.body.position, pose));
            applyOnHit(t, weapon, enemy, slot);
            if (dealt > 0 || target.shield > 0) {
                knockbackFrom(t, enemy, slot, c.body.position, spec.knockback, spec.knockUp);
            }
            hit = enemy;
        }
    }
    for (const dep of t.s.deployables) {
        if (dep.slot === slot || dep.maxHp <= 0)
            continue;
        const d = Math.hypot(dep.position.x - c.body.position.x, dep.position.z - c.body.position.z);
        if (d <= spec.reach + 1.5)
            damageDeployable(t, dep, weapon.damage, slot);
    }
    emit(t, {
        t: 'melee',
        slot,
        weapon: weapon.id,
        position: {
            x: origin.x + dir.x * spec.reach * 0.6,
            y: origin.y,
            z: origin.z + dir.z * spec.reach * 0.6,
        },
        hit,
    });
}
export function stepMelee(t, slot, input) {
    const c = mech(t, slot);
    const weapon = meleeWeaponFor(t.ctx, defOf(t, slot));
    const spec = weapon.melee;
    c.melee.cooldown = Math.max(0, c.melee.cooldown - SIM_DT);
    const edge = input.melee && !c.lastInput.melee;
    if (edge && isLive(t))
        startMelee(t, slot);
    if (c.melee.phase === 'idle')
        return;
    if (!c.alive || t.mods[slot].stunned) {
        c.melee.phase = 'idle';
        return;
    }
    c.melee.ticks++;
    switch (c.melee.phase) {
        case 'windup':
            if (c.melee.ticks >= secondsToTicks(spec.windupSeconds)) {
                c.melee.phase = 'active';
                c.melee.ticks = 0;
            }
            break;
        case 'active':
            if (!c.melee.hit) {
                meleeHit(t, slot, weapon);
                c.melee.hit = true;
            }
            if (c.melee.ticks >= secondsToTicks(spec.activeSeconds)) {
                c.melee.phase = 'recovery';
                c.melee.ticks = 0;
            }
            break;
        case 'recovery':
            if (c.melee.ticks >= secondsToTicks(spec.recoverySeconds)) {
                c.melee.phase = 'idle';
                c.melee.ticks = 0;
                // The attack cadence is the fire rate: the cooldown tops up the animation time.
                const animation = spec.windupSeconds + spec.activeSeconds + spec.recoverySeconds;
                c.melee.cooldown = Math.max(0, 1 / weapon.fireRate - animation);
            }
            break;
    }
}
/** Splash helper shared by projectiles and deployables: torso damage with edge falloff. */
export function splashAt(t, source, point, radius, damage, minFactor, selfDamage, kind = 'splash') {
    for (const slot of [0, 1]) {
        if (slot === source && !selfDamage)
            continue;
        const c = mech(t, slot);
        if (!c.alive)
            continue;
        const cap = combatantCapsule(c, defOf(t, slot));
        const q = closestPointOnAxis(point, cap);
        const d = Math.max(0, Math.hypot(q.x - point.x, q.y - point.y, q.z - point.z) - cap.radius);
        if (d > radius)
            continue;
        const factor = 1 - (1 - minFactor) * (d / radius);
        applyDamage(t, areaPacket(t, slot, source, damage * factor, kind, point));
    }
    for (const dep of t.s.deployables) {
        if (dep.slot === source || dep.maxHp <= 0)
            continue;
        const d = Math.hypot(dep.position.x - point.x, dep.position.y - point.y, dep.position.z - point.z);
        if (d <= radius)
            damageDeployable(t, dep, damage * (1 - (1 - minFactor) * (d / radius)), source);
    }
    emit(t, { t: 'explosion', slot: source, position: { ...point }, radius });
}
//# sourceMappingURL=weapons.js.map